import { type Event, normalizeEvent } from "@packages/commons";

export const eventDetailsUrl = (source: "model" | "tenancy" | "session", eventId: string, parentId?: string) => {
  const id = encodeURIComponent(eventId).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );
  return source === "tenancy" ? `auth/events/${id}` : `${source === "model" ? "models" : "sessions"}/${encodeURIComponent(parentId || "")}/events/${id}`;
};

export const readEventDetails = (response: any): Event => {
  // Unwrap transport responses without discarding a legacy event's data payload.
  const isEvent = (value: any) =>
    value &&
    typeof value === "object" &&
    (value.eventType ||
      value.messageType ||
      value.event ||
      typeof value.status === "string");
  const event = isEvent(response) ? response : (response?.data ?? response);
  if (!event || Array.isArray(event) || !isEvent(event)) {
    throw new Error("The event detail endpoint did not return an event. Check that the events API is deployed.");
  }
  const normalized = normalizeEvent(event);
  if (!normalized) {
    throw new Error("The event detail endpoint did not return an event. Check that the events API is deployed.");
  }
  return normalized;
};

const filePathFact = (event: Event, key: string): string | undefined => {
  const value = event.facts?.find(fact => fact.key === key)?.value;
  return typeof value === "string" && value.trim() ? value : undefined;
};

const filesUrl = (event: Event, path: unknown): string | undefined => {
  if (!event.modelId || typeof path !== "string" || !path.trim()) return undefined;
  return `/monitor/files?${new URLSearchParams({ projectId: event.modelId, filePath: path })}`;
};

export const eventFileUrl = (event: Event): string | undefined => {
  const factPath = filePathFact(event, "file-path") || filePathFact(event, "error-path") || filePathFact(event, "processed-path");
  // Legacy metadata.path can be an API route on other event types.
  const legacyPath = event.eventType?.startsWith("fileProcessing.")
    ? event.meta?.errorPath || event.meta?.processedPath || event.meta?.path
    : undefined;
  return filesUrl(event, factPath || legacyPath);
};

export const eventErrorFileUrl = (event: Event): string | undefined => {
  if (event.eventType !== "fileProcessing.failedRows") return undefined;
  return filesUrl(event, filePathFact(event, "csv-error-path") || event.meta?.csvErrorPath);
};
